import { useEffect, useState, useMemo } from "react";

import { Link } from "react-router-dom";

import {

  Calendar, DollarSign, TrendingUp, Clock, CheckCircle2,

  Copy, FileText, ArrowRight, ShieldCheck, Activity, User, Euro, MessageSquare

} from "lucide-react";

import { API } from "@/lib/api";

import { useAuth } from "@/context/AuthContext";

import { Button } from "@/components/ui/button";

import { toast } from "@/components/ui/sonner";



function timeAgo(dateString) {

  if (!dateString) return "Récemment";

  const now = new Date();

  const date = new Date(dateString);

  const diffSec = Math.floor((now - date) / 1000);



  if (diffSec < 60) return "À l'instant";

  const diffMin = Math.floor(diffSec / 60);

  if (diffMin < 60) return `Il y a ${diffMin} min`;

  const diffHours = Math.floor(diffMin / 60);

  if (diffHours < 24) return `Il y a ${diffHours} h`;

  const diffDays = Math.floor(diffHours / 24);

  return `Il y a ${diffDays} j`;

}



function formatShortName(fullName) {

  if (!fullName) return "Un client";

  const parts = fullName.trim().split(" ");

  if (parts.length === 1) return parts[0];

  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;

}



export default function Dashboard() {

  const { user, tenant } = useAuth();

  const isOwner = user?.role === "owner" || user?.role === "superadmin" || !user?.role;

  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState(null);

  const [quotes, setQuotes] = useState([]);

  const [bookings, setBookings] = useState([]);

  const [services, setServices] = useState([]);



  const loadData = () => {

    Promise.all([

      API.get("/dashboard/stats"),

      API.get("/quotes"),

      API.get("/bookings"),

      API.get("/services").catch(() => ({ data: [] })),

    ])

      .then(([statsRes, quotesRes, bookingsRes, servicesRes]) => {

        setStats(statsRes.data);

        setQuotes(quotesRes.data || []);

        setBookings(bookingsRes.data || []);

        setServices(servicesRes.data || []);

      })

      .catch((err) => console.error(err))

      .finally(() => setLoading(false));

  };



  useEffect(() => {

    loadData();

  }, []);



  // Écoute les changements d'émargement effectués depuis le calendrier ou appointments

  useEffect(() => {

    const handler = () => loadData();

    window.addEventListener("bookings-changed", handler);

    return () => window.removeEventListener("bookings-changed", handler);

  }, []);



  // Map des prix des services pour matcher b.service_id

  const servicePriceMap = useMemo(() => {

    const map = {};

    services.forEach((s) => {

      map[s.id] = Number(s.price || 0);

    });

    return map;

  }, [services]);



  // Fonction infaillible de récupération du prix d'un RDV

  const getBookingPrice = (b) => {

    if (b.service_price !== undefined && b.service_price !== null) return Number(b.service_price);

    if (b.price !== undefined && b.price !== null) return Number(b.price);

    if (b.service?.price !== undefined && b.service?.price !== null) return Number(b.service.price);

    if (b.service_id && servicePriceMap[b.service_id] !== undefined) return servicePriceMap[b.service_id];

    return 0;

  };



  // Calcul du Chiffre d'Affaires Réel selon les statuts

  const financialStats = useMemo(() => {

    const now = new Date();

    let caReel = 0;

    let caPrevisionnel = 0;

    let devisEnCours = 0;



    bookings.forEach((b) => {

      const price = getBookingPrice(b);

      const rawDate = b.start_at || b.start_time || b.booking_date || b.start;

      const isPast = rawDate ? new Date(rawDate) < now : false;



      // CA Réel Encaissé = créneau marqué "completed" (honoré) OU (confirmé et passé)

      if (b.status === "completed" || (b.status === "confirmed" && isPast)) {

        caReel += price;

      } else if (b.status === "confirmed" && !isPast) {

        caPrevisionnel += price;

      }

    });



    quotes.forEach((q) => {

      if (q.status === "accepted" && q.estimated_amount) {

        devisEnCours += Number(q.estimated_amount);

      }

    });



    return {

      caReel,

      caPrevisionnel,

      devisEnCours,

      totalPotentiel: caReel + caPrevisionnel + devisEnCours

    };

  }, [bookings, quotes, servicePriceMap]);



  // Flux d'activité chronologique

  const activityFeed = useMemo(() => {

    const events = [];



    bookings.forEach((b) => {

      const price = getBookingPrice(b);

      events.push({

        id: `b-${b.id}`,

        type: "booking",

        title: `${formatShortName(b.customer_name)} a réservé un créneau`,

        subtitle: `${b.service_name || "Prestation"} ${price > 0 ? `(${price} €)` : ""}`,

        timestamp: b.created_at || b.start_at || b.start_time,

        icon: Calendar,

        iconColor: "text-blue-600 bg-blue-50 border-blue-200",

      });

    });



    quotes.forEach((q) => {

      events.push({

        id: `q-${q.id}`,

        type: "quote",

        title: `${formatShortName(q.customer_name)} a envoyé une demande de devis`,

        subtitle: q.title || "Demande personnalisée",

        timestamp: q.created_at,

        icon: FileText,

        iconColor: "text-amber-600 bg-amber-50 border-amber-200",

      });

      if (q.estimated_amount) {

        events.push({

          id: `qe-${q.id}`,

          type: "chiffrage",

          title: `Devis chiffré à ${Number(q.estimated_amount).toLocaleString("fr-FR")} €`,

          subtitle: `Pour ${formatShortName(q.customer_name)}`,

          timestamp: q.updated_at || q.created_at,

          icon: Euro,

          iconColor: "text-emerald-600 bg-emerald-50 border-emerald-200",

        });

      }

    });



    return events

      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

      .slice(0, 7);

  }, [bookings, quotes, servicePriceMap]);



  const currentSlug = tenant?.slug || (tenant?.name ? tenant.name.toLowerCase().replace(/\s+/g, "-") : "garage-dupont");

  const bookingUrl = `${window.location.origin}/b/${currentSlug}`;

  const quoteUrl = `${window.location.origin}/quote/${currentSlug}`;



  return (

    <div className="space-y-6">

      {/* En-tête */}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

        <div>

          <h1 className="font-display text-2xl font-bold text-slate-900">

            Bonjour, {tenant?.name || "Entreprise"} 👋

          </h1>

          <p className="mt-0.5 text-sm text-slate-500">

            Aperçu de vos performances en direct et flux de vos demandes.

          </p>

        </div>

        <div className="flex flex-wrap items-center gap-2">

          <Button

            size="sm"

            variant="outline"

            onClick={() => {

              navigator.clipboard.writeText(quoteUrl);

              toast.success("Lien de devis copié !");

            }}

          >

            <Copy className="mr-1.5 h-3.5 w-3.5" /> Lien Devis

          </Button>

          <Button

            size="sm"

            variant="outline"

            onClick={() => {

              navigator.clipboard.writeText(bookingUrl);

              toast.success("Lien de réservation copié !");

            }}

          >

            <Copy className="mr-1.5 h-3.5 w-3.5" /> Lien Réservation

          </Button>

        </div>

      </div>



      {/* Cartes financières */}

{/* Statistiques supérieures : Adaptées selon le rôle */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isOwner ? (
          <>
            {/* 1. CA Réel Encaissé (Visible gérant uniquement) */}
            <div className="rounded-2xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/50 to-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-emerald-800">
                  CA Réel Encaissé
                </span>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                  <ShieldCheck className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-3 font-display text-2xl font-bold text-slate-900">
                {financialStats.caReel.toLocaleString("fr-FR")} €
              </p>
              <p className="mt-1 text-xs text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Prestations honorées
              </p>
            </div>

            {/* 2. CA Prévisionnel (Visible gérant uniquement) */}
            <div className="rounded-2xl border border-blue-200/80 bg-gradient-to-br from-blue-50/40 to-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-800">
                  CA Prévisionnel
                </span>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                  <TrendingUp className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-3 font-display text-2xl font-bold text-slate-900">
                {financialStats.caPrevisionnel.toLocaleString("fr-FR")} €
              </p>
              <p className="mt-1 text-xs text-blue-600 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Créneaux confirmés à venir
              </p>
            </div>

            {/* 3. Devis Validés (Visible gérant uniquement) */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Devis Validés
                </span>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                  <FileText className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-3 font-display text-2xl font-bold text-slate-900">
                {financialStats.devisEnCours.toLocaleString("fr-FR")} €
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {quotes.filter((q) => q.status === "accepted").length} devis prêts à planifier
              </p>
            </div>
          </>
        ) : (
          <>
            {/* CARTE SECRÉTAIRE 1 : Total créneaux de la semaine */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Rendez-vous à venir
                </span>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <Clock className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-3 font-display text-2xl font-bold text-slate-900">
                {bookings.filter((b) => b.status === "confirmed").length}
              </p>
              <p className="mt-1 text-xs text-slate-500">Confirmés sur le planning</p>
            </div>

            {/* CARTE SECRÉTAIRE 2 : Devis à traiter */}
            <div className="rounded-2xl border border-amber-200/80 bg-amber-50/40 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-800">
                  Devis en attente
                </span>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                  <FileText className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-3 font-display text-2xl font-bold text-slate-900">
                {quotes.filter((q) => q.status === "pending").length}
              </p>
              <p className="mt-1 text-xs text-amber-700">Demandes clients à recontacter</p>
            </div>

            {/* CARTE SECRÉTAIRE 3 : Clients enregistrés */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Clients actifs
                </span>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                  <User className="h-4 w-4" />
                </div>
              </div>
              <p className="mt-3 font-display text-2xl font-bold text-slate-900">
                {stats?.customers_count ?? "—"}
              </p>
              <p className="mt-1 text-xs text-slate-500">Fiches contacts disponibles</p>
            </div>
          </>
        )}

        {/* 4. Rendez-vous du jour (Visible pour TOUT LE MONDE) */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              Rendez-vous du jour
            </span>
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Calendar className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-3 font-display text-2xl font-bold text-slate-900">
            {stats?.today_bookings_count ?? 0}
          </p>
          <Link to="/calendar" className="mt-1 inline-flex items-center text-xs font-semibold text-blue-600 hover:underline">
            Voir le planning <ArrowRight className="ml-1 h-3 w-3" />
          </Link>
        </div>
      </div>



      {/* 2 GRAPHIQUES VISUELS */}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">

          <div className="flex items-center justify-between">

            <h2 className="font-display text-sm font-bold text-slate-900">

              Structure du Chiffre d'Affaires

            </h2>

            <span className="text-xs font-semibold text-slate-500">

              Total : {financialStats.totalPotentiel.toLocaleString("fr-FR")} €

            </span>

          </div>



          <div className="space-y-3 pt-2">

            <div>

              <div className="flex justify-between text-xs font-medium mb-1">

                <span className="text-slate-600">CA Encaissé (Honoré)</span>

                <span className="font-bold text-emerald-700">{financialStats.caReel} €</span>

              </div>

              <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">

                <div

                  className="h-full bg-emerald-500 rounded-full transition-all duration-500"

                  style={{ width: `${financialStats.totalPotentiel > 0 ? (financialStats.caReel / financialStats.totalPotentiel) * 100 : 0}%` }}

                />

              </div>

            </div>



            <div>

              <div className="flex justify-between text-xs font-medium mb-1">

                <span className="text-slate-600">CA Prévisionnel (RDV à venir)</span>

                <span className="font-bold text-blue-700">{financialStats.caPrevisionnel} €</span>

              </div>

              <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">

                <div

                  className="h-full bg-blue-500 rounded-full transition-all duration-500"

                  style={{ width: `${financialStats.totalPotentiel > 0 ? (financialStats.caPrevisionnel / financialStats.totalPotentiel) * 100 : 0}%` }}

                />

              </div>

            </div>



            <div>

              <div className="flex justify-between text-xs font-medium mb-1">

                <span className="text-slate-600">Devis Validés en attente de pose</span>

                <span className="font-bold text-indigo-700">{financialStats.devisEnCours} €</span>

              </div>

              <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">

                <div

                  className="h-full bg-indigo-500 rounded-full transition-all duration-500"

                  style={{ width: `${financialStats.totalPotentiel > 0 ? (financialStats.devisEnCours / financialStats.totalPotentiel) * 100 : 0}%` }}

                />

              </div>

            </div>

          </div>

        </div>



        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col justify-between">

          <div className="flex items-center justify-between">

            <h2 className="font-display text-sm font-bold text-slate-900">

              Entonnoir Devis & RDV

            </h2>

            <span className="text-xs text-slate-400">Conversion globale</span>

          </div>



          <div className="grid grid-cols-3 gap-3 py-4 text-center">

            <div className="rounded-xl bg-slate-50 p-3 border border-slate-100">

              <p className="text-2xl font-bold text-slate-900">{quotes.length}</p>

              <p className="text-[11px] text-slate-500 font-medium mt-0.5">Demandes devis</p>

            </div>

            <div className="rounded-xl bg-blue-50 p-3 border border-blue-100">

              <p className="text-2xl font-bold text-blue-700">

                {quotes.filter((q) => q.status === "accepted").length}

              </p>

              <p className="text-[11px] text-blue-600 font-medium mt-0.5">Devis acceptés</p>

            </div>

            <div className="rounded-xl bg-emerald-50 p-3 border border-emerald-100">

              <p className="text-2xl font-bold text-emerald-700">{bookings.length}</p>

              <p className="text-[11px] text-emerald-600 font-medium mt-0.5">RDV confirmés</p>

            </div>

          </div>



          <div className="rounded-xl bg-slate-50/80 p-3 text-xs text-slate-600 flex items-center justify-between border border-slate-100">

            <span>Taux de conversion devis :</span>

            <strong className="text-slate-900">

              {quotes.length > 0

                ? Math.round((quotes.filter((q) => q.status === "accepted").length / quotes.length) * 100)

                : 0} %

            </strong>

          </div>

        </div>

      </div>



      {/* Grille inférieure */}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden lg:col-span-5">

          <div className="border-b border-slate-100 p-4 bg-slate-50/60 flex items-center justify-between">

            <h2 className="font-display text-sm font-bold text-slate-900 flex items-center gap-1.5">

              <Activity className="h-4 w-4 text-blue-600" /> Activités récentes

            </h2>

            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400">En direct</span>

          </div>



          <div className="divide-y divide-slate-100 p-2">

            {activityFeed.length === 0 ? (

              <p className="p-6 text-center text-xs text-slate-400">Aucune activité récente.</p>

            ) : (

              activityFeed.map((act) => {

                const IconComp = act.icon;

                return (

                  <div key={act.id} className="flex items-start gap-3 p-3 hover:bg-slate-50/70 rounded-xl transition-colors">

                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg border shrink-0 ${act.iconColor}`}>

                      <IconComp className="h-4 w-4" />

                    </div>

                    <div className="min-w-0 flex-1">

                      <p className="text-xs font-semibold text-slate-900 truncate">{act.title}</p>

                      <p className="text-[11px] text-slate-500 truncate">{act.subtitle}</p>

                    </div>

                    <span className="text-[10px] text-slate-400 shrink-0 whitespace-nowrap">

                      {timeAgo(act.timestamp)}

                    </span>

                  </div>

                );

              })

            )}

          </div>

        </div>



        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden lg:col-span-7">

          <div className="flex items-center justify-between border-b border-slate-100 p-4 bg-slate-50/60">

            <h2 className="font-display text-sm font-bold text-slate-900 flex items-center gap-1.5">

              <FileText className="h-4 w-4 text-emerald-600" /> Derniers Devis

            </h2>

            <Link to="/quotes" className="text-xs font-semibold text-blue-600 hover:underline">

              Voir tout ({quotes.length})

            </Link>

          </div>



          <div className="divide-y divide-slate-100">

            {quotes.length === 0 ? (

              <p className="p-6 text-center text-xs text-slate-400">Aucun devis enregistré.</p>

            ) : (

              quotes.slice(0, 5).map((q) => (

                <div key={q.id} className="flex items-center justify-between p-3.5 hover:bg-slate-50/60 transition-colors">

                  <div className="min-w-0 flex-1 pr-3">

                    <div className="flex items-center gap-2">

                      <span className="font-semibold text-xs text-slate-900 truncate">{q.title || "Devis"}</span>

                      {q.status === "accepted" && <span className="rounded-full bg-emerald-50 text-[10px] font-semibold text-emerald-700 px-1.5 py-0.2">Validé</span>}

                      {q.status === "pending" && <span className="rounded-full bg-amber-50 text-[10px] font-semibold text-amber-700 px-1.5 py-0.2">À traiter</span>}

                    </div>

                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">

                      {q.customer_name} {q.item_reference && `· ${q.item_reference}`}

                    </p>

</div>

                  <div className="text-right shrink-0 flex items-center gap-2">
                    {/* Statut opérationnel visible par tous les rôles */}
                    {q.status === "pending" && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 border border-amber-200/60">
                        En attente
                      </span>
                    )}
                    {q.status === "sent" && (
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 border border-blue-200/60">
                        Transmis
                      </span>
                    )}
                    {q.status === "accepted" && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 border border-emerald-200/60">
                        Validé
                      </span>
                    )}
                    {q.status === "rejected" && (
                      <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700 border border-rose-200/60">
                        Refusé
                      </span>
                    )}

                    {/* Montant chiffré : visible uniquement pour le gérant */}
                    {isOwner && q.estimated_amount !== null && q.estimated_amount !== undefined && (
                      <span className="inline-flex items-center text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/70 px-2 py-0.5 rounded">
                        {Number(q.estimated_amount).toLocaleString("fr-FR")} €
                      </span>
                    )}
                  </div>

                </div>

              ))

            )}

          </div>

        </div>

      </div>

    </div>

  );

}